import { useState } from 'react';
import { Title } from 'react-admin';
import { useQuery, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublicIcon from '@mui/icons-material/Public';

import {
  GET_COUNTRY_ROUTINGS,
  CREATE_COUNTRY_ROUTING,
  UPDATE_COUNTRY_ROUTING,
  DELETE_COUNTRY_ROUTING,
} from './payment.queries';
import { PROVIDER_LABELS, PROVIDER_COLORS, METHOD_LABELS, PaymentProvider } from './payment.types';

// Country names mapping
const COUNTRY_NAMES: Record<string, string> = {
  BJ: 'Bénin',
  TG: 'Togo',
  CI: "Côte d'Ivoire",
  SN: 'Sénégal',
  ML: 'Mali',
  BF: 'Burkina Faso',
  NE: 'Niger',
  GN: 'Guinée',
  CM: 'Cameroun',
  GA: 'Gabon',
  CG: 'Congo',
  CD: 'RD Congo',
  GH: 'Ghana',
  NG: 'Nigeria',
};

// Payment methods list
const PAYMENT_METHODS = [
  'MTN_MOBILE_MONEY',
  'MOOV_MONEY',
  'ORANGE_MONEY',
  'WAVE',
  'CARD_VISA',
  'CARD_MASTERCARD',
  'QR_CODE',
  'BANK_TRANSFER',
];

interface CountryRouting {
  id: string;
  countryCode: string;
  primaryProvider: PaymentProvider;
  fallbackProvider?: PaymentProvider;
  availableMethods: string[];
  isEnabled: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface EditDialogProps {
  routing: CountryRouting | null;
  isNew: boolean;
  open: boolean;
  onClose: () => void;
  onSave: (input: any) => void;
  loading: boolean;
}

function EditRoutingDialog({ routing, isNew, open, onClose, onSave, loading }: EditDialogProps) {
  const [countryCode, setCountryCode] = useState(routing?.countryCode ?? '');
  const [primaryProvider, setPrimaryProvider] = useState(routing?.primaryProvider ?? 'CINETPAY');
  const [fallbackProvider, setFallbackProvider] = useState<string | null>(routing?.fallbackProvider ?? null);
  const [availableMethods, setAvailableMethods] = useState<string[]>(
    routing?.availableMethods ?? ['MTN_MOBILE_MONEY', 'MOOV_MONEY']
  );
  const [isEnabled, setIsEnabled] = useState(routing?.isEnabled ?? true);
  const [notes, setNotes] = useState(routing?.notes ?? '');

  const handleMethodToggle = (method: string) => {
    setAvailableMethods((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method]
    );
  };

  const handleSave = () => {
    const input: any = {
      primaryProvider,
      fallbackProvider: fallbackProvider || undefined,
      availableMethods,
      isEnabled,
      notes: notes || undefined,
    };

    if (isNew) {
      input.countryCode = countryCode;
    }

    onSave(input);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isNew ? 'Ajouter un pays' : 'Modifier le routing'}
        {!isNew && routing && (
          <Typography variant="body2" color="text.secondary">
            Pays: {COUNTRY_NAMES[routing.countryCode] || routing.countryCode}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Country select (only for new) */}
          {isNew && (
            <FormControl fullWidth>
              <InputLabel>Pays</InputLabel>
              <Select value={countryCode} label="Pays" onChange={(e) => setCountryCode(e.target.value)}>
                {Object.entries(COUNTRY_NAMES).map(([code, name]) => (
                  <MenuItem key={code} value={code}>
                    {name} ({code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Enable toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography>Routing activé</Typography>
            <Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
          </Box>

          {/* Primary provider */}
          <FormControl fullWidth>
            <InputLabel>Provider principal</InputLabel>
            <Select
              value={primaryProvider}
              label="Provider principal"
              onChange={(e) => setPrimaryProvider(e.target.value as PaymentProvider)}
            >
              {Object.entries(PROVIDER_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: PROVIDER_COLORS[key as PaymentProvider],
                      }}
                    />
                    {label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Fallback provider */}
          <FormControl fullWidth>
            <InputLabel>Provider de secours</InputLabel>
            <Select
              value={fallbackProvider || ''}
              label="Provider de secours"
              onChange={(e) => setFallbackProvider(e.target.value || null)}
            >
              <MenuItem value="">Aucun</MenuItem>
              {Object.entries(PROVIDER_LABELS)
                .filter(([key]) => key !== primaryProvider)
                .map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: PROVIDER_COLORS[key as PaymentProvider],
                        }}
                      />
                      {label}
                    </Box>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Payment methods */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Méthodes de paiement disponibles
            </Typography>
            <Grid container spacing={1}>
              {PAYMENT_METHODS.map((method) => (
                <Grid item xs={6} key={method}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={availableMethods.includes(method)}
                        onChange={() => handleMethodToggle(method)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {METHOD_LABELS[method as keyof typeof METHOD_LABELS] || method}
                      </Typography>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Notes */}
          <TextField
            label="Notes (optionnel)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || (isNew && !countryCode)}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * CountryRoutingEditor Component
 * Allows admins to configure payment provider routing per country
 */
export function CountryRoutingEditor() {
  const [editingRouting, setEditingRouting] = useState<CountryRouting | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data, loading, refetch } = useQuery<{ countryProviderRoutings: CountryRouting[] }>(GET_COUNTRY_ROUTINGS);

  const [createRouting, { loading: createLoading }] = useMutation(CREATE_COUNTRY_ROUTING, {
    onCompleted: () => {
      setSnackbar({ open: true, message: 'Pays ajouté avec succès', severity: 'success' });
      setIsCreating(false);
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Erreur: ${error.message}`, severity: 'error' });
    },
  });

  const [updateRouting, { loading: updateLoading }] = useMutation(UPDATE_COUNTRY_ROUTING, {
    onCompleted: () => {
      setSnackbar({ open: true, message: 'Routing mis à jour avec succès', severity: 'success' });
      setEditingRouting(null);
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Erreur: ${error.message}`, severity: 'error' });
    },
  });

  const [deleteRouting, { loading: deleteLoading }] = useMutation(DELETE_COUNTRY_ROUTING, {
    onCompleted: () => {
      setSnackbar({ open: true, message: 'Pays supprimé avec succès', severity: 'success' });
      setDeletingCode(null);
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Erreur: ${error.message}`, severity: 'error' });
    },
  });

  const routings = data?.countryProviderRoutings || [];

  const handleCreate = (input: any) => {
    createRouting({ variables: { input } });
  };

  const handleUpdate = (input: any) => {
    if (!editingRouting) return;
    updateRouting({
      variables: {
        countryCode: editingRouting.countryCode,
        input,
      },
    });
  };

  const handleDelete = () => {
    if (!deletingCode) return;
    deleteRouting({ variables: { countryCode: deletingCode } });
  };

  return (
    <Box>
      <Title title="Routing par pays" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Configuration du routing par pays
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Définissez quel provider de paiement utiliser pour chaque pays
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualiser">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreating(true)}>
            Ajouter un pays
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Pays</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell>Provider principal</TableCell>
                <TableCell>Fallback</TableCell>
                <TableCell>Méthodes</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={60} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={32} height={32} />
                    </TableCell>
                  </TableRow>
                ))
              ) : routings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <PublicIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">Aucun pays configuré</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setIsCreating(true)}
                      sx={{ mt: 2 }}
                    >
                      Ajouter un pays
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                routings.map((routing) => (
                  <TableRow key={routing.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600}>{routing.countryCode}</Typography>
                        <Typography color="text.secondary">
                          {COUNTRY_NAMES[routing.countryCode] || routing.countryCode}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={routing.isEnabled ? 'Actif' : 'Inactif'}
                        size="small"
                        color={routing.isEnabled ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PROVIDER_LABELS[routing.primaryProvider]}
                        size="small"
                        sx={{
                          backgroundColor: `${PROVIDER_COLORS[routing.primaryProvider]}20`,
                          color: PROVIDER_COLORS[routing.primaryProvider],
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {routing.fallbackProvider ? (
                        <Chip
                          label={PROVIDER_LABELS[routing.fallbackProvider]}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: PROVIDER_COLORS[routing.fallbackProvider],
                            color: PROVIDER_COLORS[routing.fallbackProvider],
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {routing.availableMethods.slice(0, 3).map((method) => (
                          <Chip
                            key={method}
                            label={METHOD_LABELS[method as keyof typeof METHOD_LABELS] || method}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {routing.availableMethods.length > 3 && (
                          <Chip label={`+${routing.availableMethods.length - 3}`} size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {routing.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => setEditingRouting(routing)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeletingCode(routing.countryCode)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create/Edit dialog */}
      <EditRoutingDialog
        routing={isCreating ? null : editingRouting}
        isNew={isCreating}
        open={isCreating || Boolean(editingRouting)}
        onClose={() => {
          setIsCreating(false);
          setEditingRouting(null);
        }}
        onSave={isCreating ? handleCreate : handleUpdate}
        loading={createLoading || updateLoading}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deletingCode)} onClose={() => setDeletingCode(null)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le routing pour{' '}
            <strong>{COUNTRY_NAMES[deletingCode || ''] || deletingCode}</strong> ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingCode(null)}>Annuler</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CountryRoutingEditor;
